import { useEffect, useState } from "react";
import { api, authedUrl } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Command, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import EmployeeCombobox from "@/components/EmployeeCombobox";
import LookupCombo from "@/components/LookupCombo";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Check, X } from "lucide-react";
import { toast } from "sonner";
import { BranchSelect } from "@/components/BranchBits";
import { useAuth } from "@/context/AuthContext";

const empty = { name: "", description: "", department: "", lead_id: "", member_ids: [], branch_id: "" };

/** Searchable multi-select for team members. */
function MemberPicker({ employees, value, onChange, testid = "team-members" }) {
  const [q, setQ] = useState("");
  const selected = new Set(value);
  const filtered = employees.filter((e) => {
    if (!q) return true;
    const qq = q.toLowerCase();
    return `${e.full_name} ${e.email} ${e.department} ${e.designation}`.toLowerCase().includes(qq);
  });
  return (
    <div className="rounded-md border">
      {value.length > 0 && (
        <div className="p-2 flex flex-wrap gap-1 border-b">
          {value.map((id) => {
            const e = employees.find((x) => x.id === id);
            if (!e) return null;
            return (
              <span key={id} className="inline-flex items-center gap-1 rounded-full bg-secondary px-2 py-0.5 text-xs" data-testid={`${testid}-chip-${id}`}>
                {e.full_name}
                <button type="button" onClick={() => onChange(value.filter((x) => x !== id))}><X className="w-3 h-3" /></button>
              </span>
            );
          })}
        </div>
      )}
      <Command shouldFilter={false} className="rounded-none">
        <CommandInput placeholder="Search employees…" value={q} onValueChange={setQ} data-testid={`${testid}-search`} />
        <CommandList className="max-h-56">
          <CommandGroup>
            {filtered.map((e) => (
              <CommandItem
                key={e.id}
                value={e.id}
                data-testid={`${testid}-opt-${e.id}`}
                onSelect={() => onChange(selected.has(e.id) ? value.filter((x) => x !== e.id) : [...value, e.id])}
                className="gap-2"
              >
                <Avatar className="h-6 w-6"><AvatarImage src={authedUrl(e.avatar_url)} /><AvatarFallback className="text-[9px]">{e.full_name.split(" ").map(s=>s[0]).slice(0,2).join("")}</AvatarFallback></Avatar>
                <div className="flex-1 min-w-0"><div className="text-sm truncate">{e.full_name}</div><div className="text-[10px] text-muted-foreground truncate">{e.designation || e.department}</div></div>
                <Check className={`w-4 h-4 ${selected.has(e.id) ? "opacity-100" : "opacity-0"}`} />
              </CommandItem>
            ))}
          </CommandGroup>
        </CommandList>
      </Command>
    </div>
  );
}

export default function TeamFormDialog({ open, onOpenChange, team, onSaved }) {
  const { user } = useAuth();
  const [form, setForm] = useState(empty);
  const [employees, setEmployees] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(team ? { ...empty, ...team } : empty);
    api.get("/employees").then((r) => setEmployees(r.data.filter((e) => e.id !== "tappy-bot")));
  }, [open, team]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.department) { toast.error("Name & Department are required"); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.lead_id) delete payload.lead_id;
      // Only the Company Admin picks the branch (blank = company-wide); Branch Admins are fixed to their own.
      if (user?.role === "admin") payload.branch_id = payload.branch_id || null;
      else delete payload.branch_id;
      if (team?.id) {
        await api.patch(`/teams/${team.id}`, payload);
        toast.success("Team updated");
      } else {
        await api.post("/teams", payload);
        toast.success("Team created");
      }
      onSaved?.();
      onOpenChange(false);
    } catch (e2) { toast.error(e2?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle data-testid="team-form-title">{team ? "Edit Team" : "New Team"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Team Name *</Label><Input data-testid="tf-name" value={form.name} onChange={(e) => set("name", e.target.value)} /></div>
            <div><Label>Department *</Label><LookupCombo testid="tf-dept" kind="departments" value={form.department} onChange={(v) => set("department", v)} placeholder="Select department…" /></div>
            <div className="col-span-2"><BranchSelect value={form.branch_id} onChange={(v) => set("branch_id", v)} /></div>
            <div className="col-span-2"><Label>Description</Label><Textarea rows={2} data-testid="tf-desc" value={form.description || ""} onChange={(e) => set("description", e.target.value)} /></div>
            <div className="col-span-2">
              <Label>Team Lead</Label>
              <EmployeeCombobox
                testid="tf-lead" employees={employees} value={form.lead_id}
                onChange={(v) => set("lead_id", v)}
              />
            </div>
            <div className="col-span-2">
              <Label>Members ({form.member_ids.length})</Label>
              <MemberPicker employees={employees} value={form.member_ids} onChange={(v) => set("member_ids", v)} />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="team-save-btn">{saving ? "Saving…" : (team ? "Save changes" : "Create team")}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
